import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import AdminDashboard from './AdminDashboard';
import AdminUsers from './AdminUsers';
import AdminChatHistory from './AdminChatHistory';
import AdminAuditLog from './AdminAuditLog';
import AdminEmailLog from './AdminEmailLog';
import AdminOps from './AdminOps';
import AdminTrading from './AdminTrading';

// Admin console hub: a single page with tabs (Users / AI Chat History / Analysis)
// instead of separate standalone pages. Each tab embeds its existing component
// (embedded=true strips the component's own shell/header). Refresh is handled
// per-tab inside each embedded component.
//
// Tabs:
//   users    -> AdminUsers (member management)
//   chat     -> AdminChatHistory (Ask-AI transcripts)
//   analysis -> stocks analyzed by users (coming soon — needs a per-user
//               analyze-log backend before it can show data)

const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'users', label: 'Users' },
  { key: 'chat', label: 'AI Chat History' },
  { key: 'audit', label: 'Audit Log' },
  { key: 'email', label: 'Email Audit' },
  { key: 'ops', label: 'Operations' },
  { key: 'trading', label: 'Trading' },
  { key: 'analysis', label: 'Analysis', disabled: true },
];

const Admin = () => {
  const location = useLocation();
  // Deep-link: /admin/chat opens the Chat tab; everything else opens the Dashboard.
  const initial = location.pathname.endsWith('/chat') ? 'chat' : 'dashboard';
  const [tab, setTab] = useState(initial);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
          <p className="text-sm text-gray-500">User management &amp; account administration</p>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-6" aria-label="Admin sections">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  disabled={t.disabled}
                  onClick={() => !t.disabled && setTab(t.key)}
                  className={`whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium ${
                    active
                      ? 'border-purple-600 text-purple-700'
                      : t.disabled
                      ? 'border-transparent text-gray-300 cursor-not-allowed'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  title={t.disabled ? 'Coming soon' : undefined}
                >
                  {t.label}
                  {t.disabled && <span className="ml-1 text-[10px] uppercase tracking-wide">(soon)</span>}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Panels */}
        {tab === 'dashboard' && <AdminDashboard embedded />}
        {tab === 'users' && <AdminUsers embedded />}
        {tab === 'chat' && <AdminChatHistory embedded />}
        {tab === 'audit' && <AdminAuditLog embedded />}
        {tab === 'email' && <AdminEmailLog embedded />}
        {tab === 'ops' && <AdminOps embedded />}
        {tab === 'trading' && <AdminTrading embedded />}
        {tab === 'analysis' && (
          <div className="bg-white rounded-lg shadow-sm p-10 text-center">
            <p className="text-gray-800 font-medium mb-1">Analysis — coming soon</p>
            <p className="text-sm text-gray-500">
              A view of stocks analyzed by users will appear here once per-user analyze logging is in place.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
